import { useEffect, useMemo } from 'react';
import { Chart } from '@antv/g2';
import { isH5 } from '@web3/base-utils';
import { useFm } from '@web3/base-hooks';
interface FundDistributionPieChartProps {
  frozenAmount: string;
  awaitingResponseAmount: string;
  trackingAmount: string;
  totalHackedAmount: string;
}

export const FundDistributionPieChart = (
  props: FundDistributionPieChartProps
) => {
  const {
    frozenAmount = 0,
    awaitingResponseAmount = 0,
    trackingAmount = 0,
    totalHackedAmount = 1_400_000_000,
  } = props;
  const fm = useFm();
  // 总金额, 三个金额相加
  const totalAmount = useMemo(() => {
    return (
      Number(frozenAmount) +
      Number(awaitingResponseAmount) +
      Number(trackingAmount)
    );
  }, [frozenAmount, awaitingResponseAmount, trackingAmount]);

  // 数据
  const data = useMemo(() => {
    return [
      {
        item: fm('tracking'),
        count: trackingAmount,
        // 保存实际百分比用于显示
        percent: Number(trackingAmount) / Number(totalAmount),
        // 确保视觉上至少显示1%的占比，但不影响实际数据
        displayPercent: Math.max(
          Number(trackingAmount) / Number(totalAmount),
          0.01
        ),
        color: '#40E0D0',
      },
      {
        item: fm('freezedFunds'),
        count: frozenAmount,
        percent: Number(frozenAmount) / Number(totalAmount),
        displayPercent: Math.max(
          Number(frozenAmount) / Number(totalAmount),
          0.01
        ),
        color: '#9370DB',
      },
      {
        item: fm('awaitingResponse2'),
        count: awaitingResponseAmount,
        percent: Number(awaitingResponseAmount) / Number(totalAmount),
        displayPercent: Math.max(
          Number(awaitingResponseAmount) / Number(totalAmount),
          0.01
        ),
        color: '#FF0000',
      },
    ];
  }, [fm, trackingAmount, totalAmount, frozenAmount, awaitingResponseAmount]);

  useEffect(() => {
    const chart = new Chart({
      container: 'container',
      height: 288,
      width: 288,
      autoFit: true,
    });

    // 设置坐标系
    chart.coordinate({
      type: 'theta',
      innerRadius: 0.75,
      startAngle: -Math.PI,
      endAngle: Math.PI,
    });

    // 添加数据 - 使用displayPercent来绘制图表，确保小于1%的部分也能在视觉上有足够的显示空间
    chart
      .interval()
      .transform({ type: 'stackY' })
      .data(data)
      .encode('y', 'displayPercent') // 使用displayPercent来控制图形显示大小
      .encode('color', 'item')
      .style('stroke', '#000000')
      .style('strokeWidth', 1)
      .style('inset', 1)
      .scale('color', {
        range: data.map((d) => d.color),
      })
      .tooltip(false);

    // 移除图例
    chart.legend(false);

    // 渲染图表
    chart.render();

    // 添加外部标签 - 使用实际的percent来显示准确的数据
    if (!isH5) {
      data.forEach((item, index) => {
        const container = document.getElementById('container');
        const label = document.createElement('div');
        label.style.position = 'absolute';
        label.style.color = '#FFFFFF';
        label.style.fontSize = '12px';
        label.style.fontStyle = 'normal';
        label.style.fontWeight = '500';
        label.style.lineHeight = 'normal';
        label.style.maxWidth = '120px';
        label.style.lineHeight = '1.2';
        const formattedCount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(Number(item.count));

        const formattedPercent = (item.percent * 100).toFixed(2);

        label.innerHTML = `${formattedPercent}% ${item.item}<br><span style="font-size: 10px;color: #ADB1B8;">${formattedCount}</span>`;

        // 根据不同项目设置标签位置;
        const positions = isH5
          ? [
              { right: '-50px', top: '5%' }, // Tracking
              { left: '-30px', bottom: '10%' }, // Freezed Funds
              { left: '-40px', top: '10%' }, // Awaiting response
            ]
          : [
              { right: '-75px', top: '5%' }, // Being Tracked
              { left: '-50px', bottom: '15%' }, // Freezed Funds
              { left: '-80px', bottom: '38%' }, // Awaiting response
            ];

        const position = positions[index];
        if (position) {
          Object.assign(label.style, position);
        }

        container?.appendChild(label);
      });
    }

    // 添加中心标签
    const container = document.getElementById('container');
    const centerLabel = document.createElement('div');
    centerLabel.style.position = 'absolute';
    centerLabel.style.left = '50%';
    centerLabel.style.top = '50%';
    centerLabel.style.transform = 'translate(-50%, -50%)';
    centerLabel.style.color = '#FFFFFF';
    centerLabel.style.fontSize = '18px';
    centerLabel.style.fontWeight = 'bold';
    centerLabel.style.textAlign = 'center';
    centerLabel.style.lineHeight = '1.2';

    const allAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(Number(totalHackedAmount));

    centerLabel.innerHTML = `${allAmount}<br><span style="font-size: ${
      isH5 ? '14px' : '16px'
    };">${fm('totalHacked')}</span>`;

    container?.appendChild(centerLabel);

    return () => {
      chart.destroy();
      const container = document.getElementById('container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [data, fm, totalAmount, totalHackedAmount]);

  return (
    <>
      <div
        id="container"
        className="w-full h-full flex justify-center items-center pointer-events-none"
        style={{ backgroundColor: '#000000' }}
      ></div>
      <div className="md:hidden overflow-x-auto w-screen px-4">
        <div
          className="flex justify-between mt-4 whitespace-nowrap p-4 rounded-lg bg-[#16171A] gap-3 w-fit"
          id="tooltip"
        >
          <div className="flex flex-col items-start border-r border-[#2C2D31] pr-3">
            <div className="text-white flex items-center">
              <div className="w-3 h-3 rounded-full bg-[#FF0000] mr-2"></div>
              <div>
                <span>
                  {(
                    (Number(awaitingResponseAmount) / totalAmount) *
                    100
                  ).toFixed(2)}
                  %
                </span>
                <span> {fm('awaitingResponse2')}</span>
              </div>
            </div>
            <div className="text-[#ADB1B8] text-sm ml-5">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(Number(awaitingResponseAmount))}
            </div>
          </div>
          <div className="flex flex-col items-start  border-r border-[#2C2D31] pr-3">
            <div className="text-white flex items-center">
              <div className="w-3 h-3 rounded-full bg-[#9370DB] mr-2"></div>
              <div>
                <span>
                  {((Number(frozenAmount) / totalAmount) * 100).toFixed(2)}%
                </span>
                <span> {fm('freezedFunds')}</span>
              </div>
            </div>
            <div className="text-[#ADB1B8] text-sm ml-5">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(Number(frozenAmount))}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <div className="text-white flex items-center">
              <div className="w-3 h-3 rounded-full bg-[#40E0D0] mr-2"></div>
              <div>
                <span>
                  {((Number(trackingAmount) / totalAmount) * 100).toFixed(2)}%
                </span>
                <span> {fm('tracking')}</span>
              </div>
            </div>
            <div className="text-[#ADB1B8] text-sm ml-5">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(Number(trackingAmount))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
